import { createTheme, Theme } from '@mui/material/styles';
import { DesignTokens, ThemeMode, getTokens } from './tokens';
import './types';

export type { DesignTokens, ThemeMode, Tone } from './tokens';
export { getTokens, lightTokens, darkTokens } from './tokens';

/** `theme.custom.mq.*` without the `@media` prefix, for MUI's `useMediaQuery`. */
export const mediaQuery = (jssQuery: string): string => jssQuery.replace(/^@media\s*/, '');

const headCellStyle = (t: DesignTokens) => ({
    fontSize: 11,
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: t.color.text.secondary,
    backgroundColor: t.color.surfaceRaised,
    borderBottom: `1px solid ${t.color.border}`,
    whiteSpace: 'nowrap' as const,
});

/**
 * MUI theme for one mode, built from the design tokens. Component overrides give every page the
 * new look (radius, borders, table heads, inputs, dialogs) without touching the page itself.
 */
export const getTheme = (mode: ThemeMode): Theme => {
    const t = getTokens(mode);
    const { color, radius, shadow, font, motion } = t;

    return createTheme({
        custom: t,
        palette: {
            mode,
            primary: { main: color.primary.main, dark: color.primary.hover, contrastText: color.primary.contrast },
            secondary: { main: color.secondary.main, dark: color.secondary.hover, contrastText: color.secondary.contrast },
            success: { main: color.tone.success.main, contrastText: color.tone.success.contrast },
            warning: { main: color.tone.warning.main, contrastText: color.tone.warning.contrast },
            error: { main: color.tone.danger.main, contrastText: color.tone.danger.contrast },
            info: { main: color.tone.info.main, contrastText: color.tone.info.contrast },
            background: { default: color.canvas, paper: color.surfaceRaised },
            text: { primary: color.text.primary, secondary: color.text.secondary, disabled: color.text.muted },
            divider: color.border,
            action: {
                hover: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(27, 23, 48, 0.04)',
                selected: color.primary.soft,
                disabled: color.text.muted,
                disabledBackground: mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(27, 23, 48, 0.06)',
                focus: color.primary.soft,
            },
        },
        shape: { borderRadius: radius.md },
        // Heading variants keep MUI's default sizes on purpose: pages that are not migrated yet use
        // `Typography variant="h3..h6"` for KPI numbers and titles. The design system's own scale
        // (24/20/16/11) lives in PageHeader, Panel and the JSS styles of migrated pages.
        typography: {
            fontFamily: font.family,
            fontSize: 14,
            body1: { fontSize: 14, lineHeight: 1.5 },
            caption: { fontSize: 12, lineHeight: 1.4, color: color.text.secondary },
            overline: { fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', lineHeight: 1.4, textTransform: 'uppercase' },
            button: { fontSize: 14, fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    html: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
                    body: {
                        backgroundColor: color.canvas,
                        color: color.text.primary,
                        fontFamily: font.family,
                        fontSize: 14,
                        lineHeight: 1.5,
                    },
                    'h1, h2, h3, h4, h5, h6': { textWrap: 'balance' },
                    ':focus-visible': { outline: `2px solid ${color.focusRing}`, outlineOffset: 2 },
                    '::selection': { backgroundColor: color.primary.soft, color: color.text.primary },
                    a: { color: color.primary.main },
                },
            },
            MuiPaper: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    // Only the default (flat) Paper becomes a card. Elevated Papers, such as the date-picker
                    // popper, keep MUI's opaque `background.paper` and shadow so they float correctly.
                    root: { backgroundImage: 'none' },
                    elevation0: {
                        backgroundColor: color.surface,
                        border: `1px solid ${color.border}`,
                        borderRadius: radius.lg,
                        boxShadow: shadow.sm,
                    },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        borderRadius: radius.md,
                        padding: '8px 14px',
                        minHeight: 40,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        transitionProperty: 'background-color, border-color, color, transform, box-shadow',
                        transitionDuration: motion.fast,
                        transitionTimingFunction: motion.ease,
                        '&:active': { transform: 'scale(0.98)' },
                    },
                    sizeSmall: { minHeight: 32, padding: '4px 10px', fontSize: 13, borderRadius: radius.sm },
                    sizeLarge: { minHeight: 46, padding: '10px 20px', fontSize: 15 },
                    containedPrimary: {
                        '&:hover': { backgroundColor: color.primary.hover },
                        '&:active': { backgroundColor: color.primary.active },
                    },
                    outlined: {
                        borderColor: color.borderStrong,
                        backgroundColor: mode === 'dark' ? 'transparent' : color.surface,
                        '&:hover': { backgroundColor: color.primary.soft, borderColor: color.primary.main },
                    },
                    outlinedError: {
                        borderColor: color.borderStrong,
                        '&:hover': { backgroundColor: color.tone.danger.soft, borderColor: color.tone.danger.main },
                    },
                    textPrimary: { '&:hover': { backgroundColor: color.primary.soft } },
                    startIcon: { marginLeft: -2, marginRight: 6 },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        borderRadius: radius.sm,
                        transitionProperty: 'background-color, color',
                        transitionDuration: motion.fast,
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: radius.sm, fontWeight: 600, fontSize: 12, height: 26 },
                    sizeSmall: { height: 22, fontSize: 11 },
                    label: { paddingLeft: 10, paddingRight: 10 },
                },
            },
            MuiTableContainer: {
                styleOverrides: { root: { borderRadius: 0, border: 'none', boxShadow: 'none', backgroundColor: 'transparent' } },
            },
            MuiTable: { styleOverrides: { root: { borderCollapse: 'separate', borderSpacing: 0 } } },
            MuiTableHead: { styleOverrides: { root: { '& .MuiTableCell-root': headCellStyle(t) } } },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        fontSize: 14,
                        lineHeight: 1.4,
                        padding: '12px 16px',
                        borderBottom: `1px solid ${color.border}`,
                        color: color.text.primary,
                        fontVariantNumeric: 'tabular-nums',
                    },
                    head: headCellStyle(t),
                    stickyHeader: { backgroundColor: color.surfaceRaised },
                    sizeSmall: { padding: '8px 12px' },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        transitionProperty: 'background-color',
                        transitionDuration: motion.fast,
                        '&.MuiTableRow-hover:hover': { backgroundColor: color.primary.soft },
                    },
                },
            },
            MuiTablePagination: {
                styleOverrides: {
                    root: { borderTop: `1px solid ${color.border}`, color: color.text.secondary, fontSize: 13 },
                    toolbar: { minHeight: 52, paddingLeft: 16 },
                    selectLabel: { fontSize: 13 },
                    displayedRows: { fontSize: 13, fontVariantNumeric: 'tabular-nums' },
                    select: { borderRadius: radius.sm },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: radius.md,
                        backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : color.surface,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: color.borderStrong },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: color.primary.main },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2, borderColor: color.primary.main },
                        '&.Mui-disabled': { backgroundColor: color.surfaceMuted },
                    },
                },
            },
            MuiInputLabel: { styleOverrides: { root: { color: color.text.secondary } } },
            MuiFormHelperText: { styleOverrides: { root: { marginLeft: 2 } } },
            MuiSelect: { styleOverrides: { icon: { color: color.text.secondary } } },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        backgroundColor: color.surfaceRaised,
                        border: `1px solid ${color.border}`,
                        borderRadius: radius.md,
                        boxShadow: shadow.lg,
                        marginTop: 4,
                    },
                    list: { padding: 6 },
                },
            },
            MuiPopover: {
                styleOverrides: { paper: { backgroundColor: color.surfaceRaised, boxShadow: shadow.lg, border: `1px solid ${color.border}` } },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        borderRadius: radius.sm,
                        minHeight: 38,
                        fontSize: 14,
                        '&.Mui-selected': { backgroundColor: color.primary.soft, color: color.primary.onSoft },
                        '&.Mui-selected:hover': { backgroundColor: color.primary.soft },
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        backgroundColor: color.surfaceRaised,
                        borderRadius: radius.xl,
                        border: `1px solid ${color.border}`,
                        boxShadow: shadow.lg,
                        backgroundImage: 'none',
                    },
                },
            },
            MuiBackdrop: {
                styleOverrides: { root: { backgroundColor: color.overlay, '&.MuiBackdrop-invisible': { backgroundColor: 'transparent' } } },
            },
            MuiDialogTitle: {
                styleOverrides: { root: { fontSize: 18, fontWeight: 600, padding: '22px 24px 8px', lineHeight: 1.3 } },
            },
            MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px 16px' } } },
            MuiDialogActions: { styleOverrides: { root: { padding: '8px 24px 20px', gap: 8 } } },
            MuiTabs: {
                styleOverrides: {
                    root: { minHeight: 44 },
                    indicator: { height: 2, borderRadius: 2, backgroundColor: color.primary.main },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        minHeight: 44,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 14,
                        color: color.text.secondary,
                        padding: '10px 14px',
                        minWidth: 0,
                        '&.Mui-selected': { color: color.text.primary },
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: color.tooltip.bg,
                        color: color.tooltip.text,
                        fontSize: 12,
                        fontWeight: 500,
                        borderRadius: radius.sm,
                        padding: '6px 10px',
                    },
                    arrow: { color: color.tooltip.bg },
                },
            },
            MuiSkeleton: {
                styleOverrides: { root: { backgroundColor: color.skeleton, borderRadius: radius.md } },
            },
            MuiCheckbox: { styleOverrides: { root: { color: color.text.secondary } } },
            MuiSwitch: { styleOverrides: { root: { padding: 8 } } },
            MuiAlert: {
                styleOverrides: {
                    root: { borderRadius: radius.md, border: `1px solid ${color.border}`, fontSize: 14 },
                },
            },
            MuiAccordion: {
                styleOverrides: { root: { backgroundImage: 'none' } },
            },
            MuiLink: { defaultProps: { underline: 'hover' } },
        },
    });
};
